import React from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaCheck, FaExclamationTriangle } from "react-icons/fa";

const Terms = () => {
  return (
    <div className="min-h-screen bg-gray-50 pt-20 md:pt-24 pb-12">
      <div className="w-full max-w-[1180px] mx-auto px-4 md:px-6">
        {/* Back Button */}
        <div className="mb-3 md:mb-6">
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center text-primary hover:text-primary-dark transition-colors font-medium group"
          >
            <FaArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" />
            Go Back
          </button>
        </div>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-5 md:p-8 mb-4 md:mb-8">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2 md:mb-4">
            Terms and Conditions
          </h1>
          <p className="text-gray-600 text-base md:text-lg">
            Welcome to CabZigo. These terms and conditions outline the rules
            and regulations for the use of CabZigo's services.
          </p>
          <div className="mt-4 text-sm text-gray-500">
            Last updated: 15 Aug 2025
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-5 md:p-8">
          {/* Section 1 */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaCheck className="text-green-500 mr-3" />
              1. Acceptance of Terms
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              By accessing and using CabZigo services, you accept and agree to
              be bound by the terms and provision of this agreement. If you do not
              agree to abide by the above, please do not use this service.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaCheck className="text-green-500 mr-3" />
              2. Service Description
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              CabZigo is a technology platform that connects passengers with
              independent third-party transportation providers. We facilitate
              ride-hailing services through our mobile application and website.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-2">Our Services Include:</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  One-way and round-trip cab bookings
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  Real-time ride tracking
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  Multiple payment options
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  24/7 customer support
                </li>
              </ul>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaCheck className="text-green-500 mr-3" />
              3. User Obligations
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">For Passengers:</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Provide accurate pickup and drop-off information
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Be punctual for scheduled rides
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Treat drivers with respect and courtesy
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Pay the fare as agreed upon booking
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">For Drivers:</h3>
                <ul className="space-y-2 text-gray-700 ml-4">
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Maintain valid driving license and vehicle registration
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Ensure vehicle safety and cleanliness
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Provide professional and courteous service
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Follow traffic rules and regulations
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaCheck className="text-green-500 mr-3" />
              4. Booking and Payment Terms
            </h2>
            <div className="space-y-4 text-gray-700">
              <p>
                <strong>Booking Confirmation:</strong> All bookings are subject to
                driver availability and confirmation. We reserve the right to cancel
                bookings in case of unavailability.
              </p>
              <p>
                <strong>Pricing:</strong> Fares are calculated based on distance,
                time, and demand. Additional charges may apply for tolls, parking,
                and waiting time.
              </p>
              <p>
                <strong>Payment:</strong> We accept multiple payment methods including
                cash, cards, UPI, and digital wallets. Payment is due upon completion
                of the trip.
              </p>
            </div>
          </section>

          {/* Section 5 */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaExclamationTriangle className="text-yellow-500 mr-3" />
              5. Cancellation Policy
            </h2>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <ul className="space-y-2 text-gray-700">
                <li>
                  <strong>Free Cancellation:</strong> Within 5 minutes of booking
                </li>
                <li>
                  <strong>Cancellation Fee:</strong> ₹20 after 5 minutes of booking
                </li>
                <li>
                  <strong>Driver Started:</strong> ₹50 if driver has started towards pickup
                </li>
                <li>
                  <strong>No Show:</strong> Full fare charged for customer no-show
                </li>
              </ul>
            </div>
          </section>

          {/* Section 6 */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaExclamationTriangle className="text-yellow-500 mr-3" />
              6. Limitation of Liability
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              CabZigo acts as an intermediary between passengers and drivers.
              We are not liable for any damages, losses, or injuries that may
              occur during the ride. Our liability is limited to the fare amount
              paid for the service.
            </p>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-red-700 text-sm">
                <strong>Important:</strong> Users are advised to have appropriate
                insurance coverage. CabZigo is not responsible for personal
                belongings left in vehicles.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaCheck className="text-green-500 mr-3" />
              7. Privacy and Data Protection
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We are committed to protecting your privacy. Personal information
              collected is used solely for providing our services and improving
              user experience. For detailed information, please refer to our{" "}
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaCheck className="text-green-500 mr-3" />
              8. Dispute Resolution
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Any disputes arising from the use of our services shall be resolved
              through binding arbitration in Mumbai, India. The laws of India
              shall govern these terms and conditions.
            </p>
          </section>

          {/* Section 9 */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaCheck className="text-green-500 mr-3" />
              9. Modifications to Terms
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              CabZigo reserves the right to modify these terms and conditions
              at any time. Users will be notified of significant changes via
              email or app notifications. Continued use of the service constitutes
              acceptance of the modified terms.
            </p>
          </section>

          {/* Contact Section */}
          <section className="bg-gray-50 p-4 md:p-6 rounded-lg">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4">
              Contact Information
            </h2>
            <p className="text-gray-700 mb-4">
              If you have any questions about these Terms and Conditions, please
              contact us:
            </p>
            <div className="space-y-2 text-gray-700">
              <p>
                <strong>Email:</strong> info@rajeshwarinfotech.com
              </p>
              <p>
                <strong>Phone:</strong> +91 7096386636
              </p>
              <p>
                <strong>Address:</strong> Rajkot, Gujarat, India
              </p>
            </div>
          </section>
        </div>

        {/* Related Links */}
        <div className="mt-4 md:mt-8 text-center">
          <div className="inline-flex space-x-4">
            <Link
              to="/privacy"
              className="text-primary hover:text-primary-dark underline"
            >
              Privacy Policy
            </Link>
            <Link
              to="/refund"
              className="text-primary hover:text-primary-dark underline"
            >
              Refund Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
